import {type DefaultError, type UseMutationOptions, useMutation} from '@tanstack/react-query'
import {createSuggestRepository} from '@yandex-delivery/api/repo/suggest'
import {createSuggestService} from '@yandex-delivery/api/service/suggest'
import type {SelectOptionItem} from '@yandex-delivery/blocks'
import {useI18n} from '@yandex-delivery/core/i18n'
import {allPass, applySpec, flow, get, identity, ifElse, join, map, trim} from '@yandex-delivery/utils'
import {useMemo} from 'react'

import {useConfigContext} from '../../contexts'
import type {LocationDetectedVariant, LocationRequest, PickupPoints, PickupStation} from './model/types'
import {useFetch} from './services/fetch'
import {
  createPickupRouteService,
  fetchPickUpDropOffPoints,
  selectPickupPointParams,
} from './services/pickupPointService'
import {fetchDetectLocationByCity} from './services/pickupPointService/fetch-detect-location'
import type {FetchPickupPointsParams} from './services/pickupPointService/types'
import {withCountryCode, withExternalApiPrefix} from './utils/fetchWith'

const useService = () => {
  const fetch = useFetch()
  const i18n = useI18n()
  const {countryCode} = useConfigContext()

  return useMemo(() => {
    const suggestRepository = createSuggestRepository({
      fetch: withCountryCode(countryCode, withExternalApiPrefix(fetch)),
    })

    return createPickupRouteService({
      params: createSuggestService(suggestRepository),
      fetch,
      countryCode,
      i18n,
    })
  }, [countryCode, fetch, i18n])
}

export const useFetchPickupPoints = (
  options?: Omit<UseMutationOptions<PickupPoints, DefaultError, FetchPickupPointsParams>, 'mutationFn'>,
) => {
  const {fetchPickupPoints} = useService()

  return useMutation({...options, mutationFn: fetchPickupPoints})
}

export function useFetchCityDesktop() {
  const fetch = useFetch()
  const {countryCode} = useConfigContext()

  return function fetchCity(params: LocationRequest) {
    return fetchDetectLocationByCity(params, fetch, countryCode)
  }
}

export function useFetchPickupPointsDesktop(isSource: boolean) {
  const fetch = useFetch()
  const {countryCode} = useConfigContext()
  const i18n = useI18n()
  const toPickupPointsBody = selectPickupPointParams(isSource)

  return useMutation({
    mutationFn(variant?: LocationDetectedVariant) {
      if (variant === undefined) {
        return Promise.resolve([])
      }

      return fetchPickUpDropOffPoints(
        toPickupPointsBody({
          geo_id: variant.geo_id,
          is_not_branded_partner_station: true,
        }),
        fetch,
        countryCode,
        i18n,
      ).then(toSelectOptions)
    },
  })
}

// @ts-expect-error TODO: Обновить типы API. Полей `street` и `house` нет в типе LocationDetails, хотя с бекенда они приходят
const isStreetAndHouseExist: (item: PickupStation['address']) => boolean = allPass([get('street'), get('house')])
const getStreetHouse: (item: PickupStation['address']) => string = flow(
  applySpec([get('street'), get('house')]),
  join(' '),
  trim,
)
const getFullAddress: (item: PickupStation) => string | undefined = flow(
  get('address'),
  ifElse(isStreetAndHouseExist, getStreetHouse, get('full_address')),
)
export const getFilterLabel: (item: PickupStation) => string = flow(
  applySpec([getFullAddress, get('name')]),
  join(' '),
  trim,
)
const toSelectOptions: (points: PickupStation[]) => SelectOptionItem<PickupStation>[] = //
  map(
    applySpec({
      label: getFilterLabel,
      value: identity,
    }),
  )
